import { useState, useEffect } from "react";
import { toast } from "sonner";
import { getBalance } from "@/lib/api";
import { formatCurrency } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import useModoAsiento, { CodigoCuenta } from "@/lib/useModoAsiento";

export default function BalanceContent({ ejercicioId }) {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  // Navegación tipo SAP: con diario (HOLDED*), cada cuenta salta a su mayor.
  const { modoAsiento, irAlMayor } = useModoAsiento(ejercicioId);

  useEffect(() => {
    loadBalance();
  }, [ejercicioId]);

  const loadBalance = async () => {
    setLoading(true);
    try {
      const response = await getBalance(ejercicioId);
      setBalance(response.data);
    } catch (error) {
      toast.error("Error al cargar balance");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Validation Badge */}
      <div className="flex justify-end">
        {balance?.cuadra ? (
          <Badge variant="outline" className="text-emerald-600 border-emerald-600">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Balance cuadrado
          </Badge>
        ) : (
          <Badge variant="outline" className="text-rose-600 border-rose-600">
            <AlertCircle className="h-3 w-3 mr-1" />
            {balance?.validacion?.mensaje || `Diferencia: ${formatCurrency(balance?.diferencia)}`}
          </Badge>
        )}
      </div>

      {/* Balance Grid */}
      <div className="grid grid-cols-1 gap-6">
        {/* ACTIVO */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between">
              <span>ACTIVO</span>
              <span className="font-mono text-lg">
                {formatCurrency(balance?.activo?.total)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <BalanceSection items={balance?.activo?.items || []} onVerMayor={modoAsiento ? irAlMayor : null} />
          </CardContent>
        </Card>

        {/* PATRIMONIO NETO Y PASIVO */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between">
              <span>PATRIMONIO NETO Y PASIVO</span>
              <span className="font-mono text-lg">
                {formatCurrency(balance?.patrimonio_neto_pasivo?.total)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <BalanceSection items={balance?.patrimonio_neto_pasivo?.items || []} onVerMayor={modoAsiento ? irAlMayor : null} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function BalanceSection({ items, onVerMayor }) {
  return (
    <div className="space-y-1">
      {items.map((item, index) => (
        <BalanceItem key={index} item={item} level={0} onVerMayor={onVerMayor} />
      ))}
    </div>
  );
}

function BalanceItem({ item, level, onVerMayor }) {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.hijos && item.hijos.length > 0;
  const hasCuentas = item.cuentas && item.cuentas.length > 0;
  const isExpandable = hasChildren || hasCuentas;

  const paddingLeft = level * 16;

  return (
    <div>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <div
            className={`flex items-center justify-between py-2 px-2 rounded hover:bg-muted/50 cursor-pointer ${
              level === 0 ? "font-semibold" : ""
            }`}
            style={{ paddingLeft: `${paddingLeft + 8}px` }}
          >
            <div className="flex items-center gap-2">
              {isExpandable && (
                <span className="text-muted-foreground">
                  {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </span>
              )}
              {!isExpandable && <span className="w-4" />}
              <span className={level === 0 ? "text-sm font-medium" : "text-sm"}>
                {item.partida}
              </span>
            </div>
            <span className={`font-mono tabular-nums text-sm ${item.saldo < 0 ? 'text-rose-600' : ''}`}>
              {formatCurrency(item.saldo)}
            </span>
          </div>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          {/* Sub-items */}
          {hasChildren && (
            <div>
              {item.hijos.map((hijo, index) => (
                <BalanceItem key={index} item={hijo} level={level + 1} onVerMayor={onVerMayor} />
              ))}
            </div>
          )}
          
          {/* Cuentas detail */}
          {hasCuentas && (
            <div className="ml-6 mt-1 mb-2 p-2 bg-muted/30 rounded text-xs">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="h-8 text-xs">Código</TableHead>
                    <TableHead className="h-8 text-xs">Nombre</TableHead>
                    <TableHead className="h-8 text-xs text-right">Saldo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {item.cuentas.map((cuenta, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="py-1 font-mono">
                        <CodigoCuenta codigo={cuenta.codigo} onVerMayor={onVerMayor} />
                      </TableCell>
                      <TableCell className="py-1 max-w-[150px] truncate">{cuenta.nombre}</TableCell>
                      <TableCell className={`py-1 text-right font-mono tabular-nums ${cuenta.saldo < 0 ? 'text-rose-600' : ''}`}>
                        {formatCurrency(cuenta.saldo)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
